import BaseModel from './BaseModel';
import { db } from '../utilities';
import { collection, doc, query, where, getDocs, addDoc, updateDoc, documentId } from 'firebase/firestore';

interface Icon {
  id: string;
  name: string;
  description: string;
  image: string;
  formId: string;
  status: boolean;
  order: number;
}

class Icons extends BaseModel {
  constructor() {
    super(collection(db, 'icons'));
  }

  async getAllIcons(formId: string | undefined): Promise<Icon[]> {
    try {
      if (!formId) {
        console.warn('formId is undefined or null. Returning empty array.');
        return [];
      }

      const snapshot = await getDocs(
        query(this.baseQuery, where('status', '==', true), where('formId', '==', formId))
      );

      if (snapshot.empty) {
        return [];
      }

      const icons = snapshot.docs.map((d) => {
        const data = d.data();
        return {
          id: d.id,
          name: data.name || "",
          description: data.description || "",
          image: data.image || "",
          formId: data.formId || formId,
          status: data.status ?? true,
          order: data.order?.order !== undefined ? data.order.order : Number.MAX_SAFE_INTEGER,
        };
      });

      return icons.sort((a, b) => a.order - b.order);

    } catch (error) {
      console.error("Error fetching icons:", error);
      throw error;
    }
  }

  async createIcon(icon: { name: string; description?: string; image?: string; formId: string; }) {
    try {
      const snapshot = await getDocs(
        query(this.baseQuery, where('formId', '==', icon.formId), where('name', '==', icon.name))
      );
      if (!snapshot.empty) {
        throw new Error('Icon name must be unique');
      }
      await addDoc(this.baseQuery, icon);
    } catch (error) {
      console.error(error);
      throw error;
    }
  }

  async updateIcon(id: string, icon: { name: string; description?: string; image?: string; formId?: string }) {
    try {
      const snapshot = await getDocs(
        query(
          this.baseQuery,
          where('name', '==', icon.name),
          where('formId', '==', icon.formId),
          where(documentId(), '!=', id)
        )
      );
      if (!snapshot.empty) {
        throw new Error('Icon name must be unique');
      }
      await updateDoc(doc(this.baseQuery, id), icon);
    } catch (error) {
      console.error(error);
      throw error;
    }
  }

  async deleteIcon(id: string) {
    try {
      await updateDoc(doc(this.baseQuery, id), { status: false });
    } catch (error) {
      console.error(error);
      throw error;
    }
  }

  async updateIconOrder(id: string, order: number): Promise<void> {
    try {
      if (!id || order === undefined) {
        throw new Error("ID and order are required.");
      }
      await updateDoc(doc(this.baseQuery, id), { order: order });
    } catch (error) {
      console.error("Error in updateIconOrder:", error);
      throw error;
    }
  }
}

export default Icons;
